import type { RoadSection, RoadSectionId } from '../domain/Reference';

// Coloring of the référentiel layer by one of its indicators (palettes and bins follow carto-b.html).
// Pure: it turns the loaded sections into a color per section and the legend entries that explain them.

export type ReferenceField =
    | 'category'
    | 'roadType'
    | 'urban'
    | 'trucks'
    | 'year'
    | 'width'
    | 'gestionary'
    | 'roadName';

// Indicator label (as listed in LAYER_INDICATORS.reference) -> the section field it colors by.
export const REFERENCE_INDICATOR_FIELDS: Record<string, ReferenceField> = {
    Catégorie: 'category',
    'Type de chaussée': 'roadType',
    Urbain: 'urban',
    'Trafic PL': 'trucks',
    Année: 'year',
    Largeur: 'width',
    Gestionnaire: 'gestionary',
    Axe: 'roadName',
};

export const CATEGORICAL_PALETTE = [
    '#334155',
    '#295EEF',
    '#0E9488',
    '#8A7CB8',
    '#B39B84',
    '#1E3A5F',
    '#8FAEF8',
    '#7C93A8',
    '#0891B2',
    '#6D28D9',
    '#B6C4D4',
    '#1E4BC8',
];
export const OTHERS_COLOR = '#94A3B8';
export const NONE_COLOR = '#CBD5E1';

const URBAN_YES_COLOR = '#1B3FA8';
const URBAN_NO_COLOR = '#C7D2DC';
const WIDTH_COLORS = ['#C7D2DC', '#8FAEF8', '#295EEF', '#1E4BC8'];
const YEAR_RAMP: [string, string] = ['#E3EAFE', '#1B3FA8'];

// Beyond this many distinct years, years are grouped by decade to keep the legend readable.
export const MAX_YEAR_ENTRIES = 8;

export type LegendEntryKind = 'value' | 'others' | 'none';

export interface LegendEntry {
    kind: LegendEntryKind;
    // Display label for a 'value' entry. 'others' and 'none' are labelled by the UI (translated).
    label: string;
    color: string;
    count: number;
    lengthMeters: number;
}

export interface ReferenceColoring {
    entries: LegendEntry[];
    colorById: Map<RoadSectionId, string>;
}

type Bucket = { key: string; label: string; color: string };
// A scale lists its colored buckets in legend order and tells which bucket a section falls in.
// `null` means the value is missing; a key that is not in `buckets` falls into "Autres".
type Scale = { buckets: Bucket[]; keyOf: (section: RoadSection) => string | null };

const lengthOf = (section: RoadSection): number => Math.abs(section.cumulEnd - section.cumulStart);

const textOf = (section: RoadSection, field: ReferenceField): string | null => {
    const value = section[field];
    if (value === null || value === undefined) {
        return null;
    }
    const text = String(value).trim();

    return text === '' ? null : text;
};

const parseHex = (hex: string): [number, number, number] => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
];

const mix = (from: string, to: string, ratio: number): string => {
    const [a, b] = [parseHex(from), parseHex(to)];

    return `#${a
        .map((channel, i) =>
            Math.round(channel + ((b[i] ?? channel) - channel) * ratio)
                .toString(16)
                .padStart(2, '0'),
        )
        .join('')}`;
};

const rampColors = (count: number): string[] =>
    Array.from({ length: count }, (_, i) => mix(YEAR_RAMP[0], YEAR_RAMP[1], count === 1 ? 1 : i / (count - 1)));

const YES = /^(oui|yes|true|vrai|1)$/i;
const NO = /^(non|no|false|faux|0)$/i;

const categoricalColor = (field: ReferenceField, value: string, index: number): string => {
    if (field === 'urban') {
        if (YES.test(value)) {
            return URBAN_YES_COLOR;
        }
        if (NO.test(value)) {
            return URBAN_NO_COLOR;
        }
    }

    return CATEGORICAL_PALETTE[index] ?? OTHERS_COLOR;
};

// One color per value, the longest (by length of network) first. Values past the palette are "Autres".
const categoricalScale = (sections: RoadSection[], field: ReferenceField): Scale => {
    const totals = new Map<string, number>();
    sections.forEach((section) => {
        const value = textOf(section, field);
        if (value !== null) {
            totals.set(value, (totals.get(value) ?? 0) + lengthOf(section));
        }
    });
    const values = Array.from(totals.entries())
        .sort(([nameA, lengthA], [nameB, lengthB]) => lengthB - lengthA || nameA.localeCompare(nameB, 'fr'))
        .slice(0, CATEGORICAL_PALETTE.length)
        .map(([value]) => value);

    return {
        buckets: values.map((value, index) => ({
            key: value,
            label: value,
            color: categoricalColor(field, value, index),
        })),
        keyOf: (section) => textOf(section, field),
    };
};

// One color per year (light to dark), or per decade when there are many distinct years.
const yearScale = (sections: RoadSection[]): Scale => {
    const years = Array.from(new Set(sections.map((section) => section.year).filter((y): y is number => y !== null)));
    years.sort((a, b) => a - b);
    const byDecade = years.length > MAX_YEAR_ENTRIES;
    const keyFor = (year: number) => String(byDecade ? Math.floor(year / 10) * 10 : year);
    const keys = Array.from(new Set(years.map(keyFor)));
    const colors = rampColors(keys.length);

    return {
        buckets: keys.map((key, index) => ({
            key,
            label: byDecade ? `${key} – ${Number(key) + 9}` : key,
            color: colors[index] ?? YEAR_RAMP[1],
        })),
        keyOf: (section) => (section.year === null ? null : keyFor(section.year)),
    };
};

const WIDTH_BINS = [
    { key: 'lt4', label: '< 4 m', test: (width: number) => width < 4 },
    { key: '4-5.5', label: '4 – 5,5 m', test: (width: number) => width >= 4 && width < 5.5 },
    { key: '5.5-7', label: '5,5 – 7 m', test: (width: number) => width >= 5.5 && width < 7 },
    { key: 'ge7', label: '> 7 m', test: (width: number) => width >= 7 },
];

const widthScale = (): Scale => ({
    buckets: WIDTH_BINS.map((bin, index) => ({
        key: bin.key,
        label: bin.label,
        color: WIDTH_COLORS[index] ?? OTHERS_COLOR,
    })),
    keyOf: (section) => (section.width === null ? null : WIDTH_BINS.find((bin) => bin.test(section.width!))!.key),
});

const scaleFor = (sections: RoadSection[], field: ReferenceField): Scale => {
    if (field === 'year') {
        return yearScale(sections);
    }
    if (field === 'width') {
        return widthScale();
    }

    return categoricalScale(sections, field);
};

// Colors every section by `indicator`. Returns null when the indicator does not color the référentiel
// (the "–" choice, or a label this layer does not know).
export const colorReferenceSections = (sections: RoadSection[], indicator: string): ReferenceColoring | null => {
    const field = REFERENCE_INDICATOR_FIELDS[indicator];
    if (!field) {
        return null;
    }
    const scale = scaleFor(sections, field);
    const known = new Map(scale.buckets.map((bucket) => [bucket.key, bucket]));
    const tally = new Map<string, { count: number; lengthMeters: number }>();
    const others = { count: 0, lengthMeters: 0 };
    const none = { count: 0, lengthMeters: 0 };
    const colorById = new Map<RoadSectionId, string>();

    sections.forEach((section) => {
        const key = scale.keyOf(section);
        const length = lengthOf(section);
        let color: string;
        let target: { count: number; lengthMeters: number };
        if (key === null) {
            color = NONE_COLOR;
            target = none;
        } else if (known.has(key)) {
            color = known.get(key)!.color;
            target = tally.get(key) ?? { count: 0, lengthMeters: 0 };
            tally.set(key, target);
        } else {
            color = OTHERS_COLOR;
            target = others;
        }
        target.count += 1;
        target.lengthMeters += length;
        colorById.set(section.id, color);
    });

    const entries: LegendEntry[] = scale.buckets
        .filter((bucket) => tally.has(bucket.key))
        .map((bucket) => ({ kind: 'value', label: bucket.label, color: bucket.color, ...tally.get(bucket.key)! }));
    if (others.count > 0) {
        entries.push({ kind: 'others', label: '', color: OTHERS_COLOR, ...others });
    }
    if (none.count > 0) {
        entries.push({ kind: 'none', label: '', color: NONE_COLOR, ...none });
    }

    return { entries, colorById };
};
